// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// The Credit Ladder page's labels and CSV exports (AUT-6617), pure so every CSVLink is fed at render: react-csv
// builds the download from its props when it renders, so an export's rows must be on its link before the click.

import moment from 'moment';

export const CONTRACT_RATE = 'contract_rate';

interface CsvHeader {
  label: string;
  key: string;
}

// The legacy exports' columns, unchanged (D8): ops has these files
export const LEGACY_CSV_HEADERS: CsvHeader[] = [
  { label: 'Org Name', key: 'orgName' },
  { label: 'Org ID', key: 'orgId' },
  { label: 'Plan Start Date', key: 'planStartDate' },
  { label: 'Plan End Date', key: 'planEndDate' },
  { label: 'Lower Limit', key: 'lowerLimit' },
  { label: 'Upper Limit', key: 'upperLimit' },
  { label: 'Current Usage', key: 'currentUsage' },
  { label: 'Price Per Credit', key: 'pricePerCredit' },
  { label: 'Plan Name', key: 'planName' },
  { label: '12 month usage', key: 'twelveMonthUsage' },
  { label: 'Starting Credits', key: 'startingCredits' },
];

export const LADDER_CSV_HEADERS: CsvHeader[] = [
  { label: 'Tier', key: 'tier' },
  { label: 'Org Name', key: 'orgName' },
  { label: 'Org ID', key: 'orgId' },
  { label: 'Standing', key: 'standing' },
  { label: 'Kind', key: 'kind' },
  // the account's one credit rate (2026-10-29): where it comes from, the figure, and the ladder tier its plan row names
  { label: 'Rate source', key: 'rateSource' },
  { label: '$/credit', key: 'accountDollarPerCredit' },
  { label: 'Ladder tier', key: 'ladderTier' },
  { label: 'Earned Tier', key: 'earnedTier' },
  { label: '12 month credits', key: 'twelveMonthCredits' },
  { label: '12 month spend ($)', key: 'twelveMonthSpendDollars' },
  { label: 'License fee', key: 'licenseFeeMode' },
  // the account's own percent (AUT-6622 D10), as a number so a sheet can sort it; the ladder column above is the workbook's 10
  { label: 'License fee %', key: 'licenseFeePercent' },
  { label: 'AEC pricing', key: 'aecPricing' },
  { label: 'AEC $/credit', key: 'aecDollarPerCredit' },
];

// One row per account on a plan or subscription: the ladder columns, then the legacy export's beside them
export const ALL_ACCOUNTS_CSV_HEADERS: CsvHeader[] = [
  ...LADDER_CSV_HEADERS,
  { label: 'Plan Name', key: 'planName' },
  { label: 'Plan Start Date', key: 'planStartDate' },
  { label: 'Plan End Date', key: 'planEndDate' },
  { label: 'Lower Limit', key: 'lowerLimit' },
  { label: 'Upper Limit', key: 'upperLimit' },
  { label: 'Current Usage', key: 'currentUsage' },
  { label: 'Price Per Credit', key: 'pricePerCredit' },
  { label: 'Starting Credits', key: 'startingCredits' },
];

const STANDING_LABELS: Record<LadderStanding, string> = {
  rate: 'own rate',
  earned: 'earned by 12-month spend',
  entry: 'default',
  payg: 'pay-as-you-go',
};

// where an account's one credit rate comes from (account_rate_util.SOURCES)
const RATE_SOURCE_LABELS: Record<string, string> = {
  contract_rate: 'contract rate',
  plan_band: 'plan band',
  ladder_tier: 'Credit Ladder tier',
  negotiated: 'negotiated',
  payg: 'pay-as-you-go',
  payg_override: 'pay-as-you-go, negotiated',
  none: '—',
};

const KIND_LABELS: Record<LadderAccountKind, string> = {
  payg: 'pay-as-you-go',
  prepay: 'pre-pay',
  precommit: 'pre-commit',
  subscription: 'subscription',
};

const AEC_PRICING_LABELS: Record<AecPricingSource, string> = {
  ladder: 'Credit Ladder',
  plan_band: 'legacy band',
  contract_rate: 'contract rate',
  payg: 'pay-as-you-go',
};

// the database and code call the ladder's Entry tier "Standard"
export const tierLabel = (name: string | null): string | null => (name === 'Standard' ? 'Entry' : name);
export const standingLabel = (standing: LadderStanding): string => STANDING_LABELS[standing] || standing;
export const rateSourceLabel = (source: string): string => RATE_SOURCE_LABELS[source] || source;
export const kindLabel = (kind: LadderAccountKind): string => KIND_LABELS[kind] || kind;
export const aecPricingLabel = (source: AecPricingSource): string => AEC_PRICING_LABELS[source] || source;
export const legacyPlanLabel = (name: string): string => (name === CONTRACT_RATE ? 'contract rate' : name);

const dollars = (amount: number): string => `$${amount.toLocaleString('en-US')}`;

// The annual-spend band a ladder row covers. Entry's band starts at the catalog's $0; the PAYG spot is the overage
// ladder's floor — usage below the Entry commitment — rather than a band of its own.
export const annualSpendBand = (row: ILadderRow): string => {
  if (row.name === 'PAYG') return `below ${dollars(row.maxAnnualSpend ?? 0)} commitment`;
  if (row.maxAnnualSpend === null) return `from ${dollars(row.minAnnualSpend)}`;
  return `${dollars(row.minAnnualSpend)} – ${dollars(row.maxAnnualSpend - 1)}`;
};

export const discountLabel = (discountPercent?: number): string => (discountPercent ? `${discountPercent.toFixed(2)}%` : '—');

// "1 own rate · 2 earned · 12 default" — the non-zero parts only
export const standingBreakdown = (orgs: ILadderAccount[]): string => {
  const count = (standing: LadderStanding) => orgs.filter((org) => org.standing === standing).length;
  return [
    [count('rate'), 'own rate'],
    [count('earned'), 'earned'],
    [count('entry'), 'default'],
  ]
    .filter(([n]) => n)
    .map(([n, label]) => `${n} ${label}`)
    .join(' · ');
};

const dateStamp = () => moment(new Date()).format('YYYY-MM-DD');

export const ladderFileName = (row: ILadderRow): string => `credit_ladder_${row.name.toLowerCase()}_${dateStamp()}.csv`;
export const allAccountsFileName = (): string => `All_Org_Details_${dateStamp()}.csv`;
export const legacyCellFileName = (planName: string, upperLimit: number | null): string => `${planName}_UpperLimit${upperLimit}_${dateStamp()}.csv`;
export const contractRateFileName = (): string => `contract_rate_${dateStamp()}.csv`;

export interface LadderCsvRow {
  tier: string | null;
  orgName: string;
  orgId: string;
  standing: string;
  kind: string;
  rateSource: string;
  accountDollarPerCredit: number | null;
  ladderTier: string | null;
  earnedTier: string | null;
  twelveMonthCredits: number;
  twelveMonthSpendDollars: number;
  licenseFeeMode: string;
  licenseFeePercent: number;
  aecPricing: string;
  aecDollarPerCredit: number | null;
}

const ladderCsvRow = (row: ILadderRow, org: ILadderAccount): LadderCsvRow => ({
  tier: row.displayName,
  orgName: org.orgName,
  orgId: org.orgId,
  standing: standingLabel(org.standing),
  kind: kindLabel(org.kind),
  rateSource: rateSourceLabel(org.rateSource),
  accountDollarPerCredit: org.accountDollarPerCredit,
  ladderTier: tierLabel(org.storedTierName),
  earnedTier: tierLabel(org.earnedTier),
  twelveMonthCredits: org.twelveMonthCredits,
  twelveMonthSpendDollars: org.twelveMonthSpendDollars,
  licenseFeeMode: org.licenseFeeMode,
  licenseFeePercent: org.licenseFeePercent,
  aecPricing: aecPricingLabel(org.aecPricing),
  aecDollarPerCredit: org.aecDollarPerCredit,
});

// one ladder row's accounts
export const ladderRows = (row: ILadderRow): LadderCsvRow[] => row.orgs.map((org) => ladderCsvRow(row, org));

export interface LegacyCsvRow {
  orgName: string;
  orgId: string;
  planStartDate: string | null;
  planEndDate: string | null;
  lowerLimit: number | null;
  upperLimit: number | null;
  currentUsage: number | null;
  pricePerCredit: number | null;
  planName: string;
  twelveMonthUsage: number;
  startingCredits: number;
}

type LegacyCellOrg = IPricingTier['plans'][string]['orgs'][number];

// one legacy band cell's accounts at the band rate (a contract-rate account at its own)
export const legacyCellRows = (planName: string, cell: { rate: number | null; orgs: LegacyCellOrg[] }, tier: IPricingTier): LegacyCsvRow[] =>
  cell.orgs.map((org) => ({
    orgName: org.orgName,
    orgId: org.orgId,
    planStartDate: org.planStartDate,
    planEndDate: org.planEndDate,
    lowerLimit: tier.lowerLimit,
    upperLimit: tier.upperLimit,
    currentUsage: org.currentUsage,
    pricePerCredit: org.pricePerCredit ?? cell.rate,
    planName: legacyPlanLabel(planName),
    twelveMonthUsage: org.twelveMonthUsage,
    startingCredits: org.startingCredits,
  }));

// every legacy cell's accounts — the all-accounts export when the server sent no ladder
export const legacyRows = (pricing: IPricingTier[]): LegacyCsvRow[] =>
  pricing.flatMap((tier) => Object.entries(tier.plans).flatMap(([planName, cell]) => legacyCellRows(planName, cell, tier)));

export type AllAccountsCsvRow = LadderCsvRow & {
  planName: string | null;
  planStartDate: string | null;
  planEndDate: string | null;
  lowerLimit: number | null;
  upperLimit: number | null;
  currentUsage: number | null;
  pricePerCredit: number | null;
  startingCredits: number;
};

// the band cell an organization sits in, for its limits; a contract-rate account has none
const bandLimitsByOrg = (pricing: IPricingTier[]): Map<string, { lowerLimit: number | null; upperLimit: number | null }> => {
  const limits = new Map<string, { lowerLimit: number | null; upperLimit: number | null }>();
  pricing.forEach((tier) => {
    Object.values(tier.plans).forEach((cell) => {
      cell.orgs.forEach((org) => limits.set(String(org.orgId), { lowerLimit: tier.lowerLimit, upperLimit: tier.upperLimit }));
    });
  });
  return limits;
};

// one row per account across every ladder row, the legacy columns joined where the account has them
export const allAccountsRows = (ladder: ILadderPricing, pricing: IPricingTier[]): AllAccountsCsvRow[] => {
  const limits = bandLimitsByOrg(pricing);
  return [...ladder.tiers, ladder.payg].flatMap((row) =>
    row.orgs.map((org) => ({
      ...ladderCsvRow(row, org),
      planName: org.planName,
      planStartDate: org.planStartDate,
      planEndDate: org.planEndDate,
      lowerLimit: limits.get(String(org.orgId))?.lowerLimit ?? null,
      upperLimit: limits.get(String(org.orgId))?.upperLimit ?? null,
      currentUsage: org.currentUsage,
      pricePerCredit: org.aecDollarPerCredit,
      startingCredits: org.startingCredits,
    })));
};

// how many accounts the legacy section holds, counted from its own cells when the ladder's count is not at hand
export const legacyAccountCount = (pricing: IPricingTier[]): number =>
  pricing.reduce((count, tier) => count + Object.values(tier.plans).reduce((n, cell) => n + (cell.orgs?.length || 0), 0), 0);
